<div class="flex flex-col h-full">
  <!-- HEADER -->
  <div class="shrink-0">
    <h2 class="text-xl font-semibold m-0">
      {{ 'support.chat.title' | translate }}
    </h2>
    @if(view() === 'list') {

    <p
      class="text-surface-500 dark:text-surface-400 mt-2 mb-3 whitespace-pre-line"
    >
      {{ 'support.chat.subtitle' | translate }}
    </p>
    <p-message severity="info" styleClass="w-full">
      <i class="fa-solid fa-envelope mr-2" aria-hidden="true"></i>
      <!-- {{ 'support.chat.emailLine' | translate }} -->
      <a [href]="'mailto:' + supportEmail" class="underline ml-1">{{
        supportEmail
      }}</a>
    </p-message>
    }
  </div>

  <p-divider></p-divider>

  @if (!supportChat.available()) {
    <!-- UNAVAILABLE -->
    <div
      class="flex-1 flex items-center justify-center text-center text-surface-500 dark:text-surface-400 px-4"
    >
      {{ 'support.chat.unavailable' | translate }}
    </div>
  } @else if (view() === 'list') {
    <!-- LIST VIEW -->
    <div class="shrink-0 mb-4">
      <p-button
        [label]="'support.chat.newConversation' | translate"
        icon="fa-solid fa-plus"
        (onClick)="startNew()"
      />
    </div>

    <div class="flex-1 overflow-y-auto flex flex-col gap-3">
      @if (supportChat.loadingList() && supportChat.tickets().length === 0) {
        <div class="text-center text-surface-400 py-10">
          <i class="fa-solid fa-spinner fa-spin fa-lg" aria-hidden="true"></i>
        </div>
      } @else if (supportChat.tickets().length === 0) {
        <div
          class="text-center text-surface-500 dark:text-surface-400 py-10 px-4"
        >
          <i
            class="fa-solid fa-comments text-4xl mb-3 block"
            aria-hidden="true"
          ></i>
          <p class="font-medium">
            {{ 'support.chat.noConversations' | translate }}
          </p>
          <p class="text-sm mt-1">
            {{ 'support.chat.noConversationsHint' | translate }}
          </p>
        </div>
      } @else {
        @for (ticket of supportChat.tickets(); track ticket.id) {
          <cosmos-ticket-list-item
            [ticket]="ticket"
            (open)="openTicket($event)"
          />
        }
      }
    </div>
  } @else {
    <!-- THREAD VIEW -->
    <!-- metadata header row (mirrors the detail-page header pattern) -->
    <div class="shrink-0 flex items-center gap-3 mb-4">
      <p-button
        icon="fa-solid fa-arrow-left"
        [outlined]="true"
        [rounded]="true"
        (onClick)="backToList()"
        [ariaLabel]="'support.chat.backToList' | translate"
      />
      @if (activeTicket(); as ticket) {
        <div class="min-w-0">
          <div class="text-lg font-bold m-0 truncate">
            @if (ticket.ticket_number) {
              {{
                'support.chat.ticketNumber'
                  | translate: { number: ticket.ticket_number }
              }}
            } @else {
              {{ 'support.chat.untitledTicket' | translate }}
            }
          </div>
          <div class="text-sm text-surface-500 dark:text-surface-400 m-0">
            {{
              'support.chat.openedOn'
                | translate: { date: (ticket.created_at | date: 'medium') }
            }}
          </div>
        </div>
        @if (activeStatusKey(); as statusKey) {
          <p-tag
            [value]="statusKey | translate"
            [severity]="activeStatusSeverity()"
          />
        }
      } @else {
        <div class="text-lg font-bold m-0">
          {{ 'support.chat.newConversation' | translate }}
        </div>
      }
    </div>

    <!-- The chat itself, styled as a card (bg + shadow + radius from PrimeNG
         card tokens) so agent bubbles contrast with the panel background.
         Not a <p-card> — its body is not flex, which breaks the column layout. -->
    <div
      class="flex-1 flex flex-col min-h-0 p-3 rounded-[var(--p-card-border-radius)] shadow-[var(--p-card-shadow)] bg-[var(--p-card-background)] text-[var(--p-card-color)]"
    >
      <div
        #scrollViewport
        class="flex-1 overflow-y-auto"
        (scroll)="onThreadScroll()"
      >
        @if (supportChat.loadingThread()) {
          <div class="text-center text-surface-400 py-10">
            <i
              class="fa-solid fa-spinner fa-spin fa-lg"
              aria-hidden="true"
            ></i>
          </div>
        } @else {
          @for (row of rows(); track row.message.localId) {
            @if (row.daySeparator) {
              <div class="flex items-center gap-3 my-4">
                <div
                  class="flex-1 h-px bg-surface-300 dark:bg-surface-700"
                ></div>
                <span
                  class="text-sm text-surface-500 dark:text-surface-400 font-medium"
                >
                  {{ row.daySeparator }}
                </span>
                <div
                  class="flex-1 h-px bg-surface-300 dark:bg-surface-700"
                ></div>
              </div>
            }
            <cosmos-message-bubble
              [message]="row.message"
              (retry)="retry($event)"
            />
          }
        }
      </div>

      <!-- COMPOSER -->
      <div
        class="shrink-0 pt-3 mt-3 border-t border-surface-300 dark:border-surface-700"
      >
        @if (isResolved()) {
          <div class="text-center text-surface-500 dark:text-surface-400 py-2">
            <p class="m-0">{{ 'support.chat.ticketResolved' | translate }}</p>
            <button
              type="button"
              class="text-primary underline mt-1"
              (click)="startNew()"
            >
              {{ 'support.chat.startNew' | translate }}
            </button>
          </div>
        } @else {
          <div class="flex items-end gap-2">
            <!-- maxHeight is an INLINE style on purpose: PrimeNG's autoResize
                 reads el.style.maxHeight directly to decide when to switch to
                 overflow-y:scroll — a Tailwind max-h-* class is invisible to it. -->
            <textarea
              pTextarea
              [autoResize]="true"
              rows="1"
              class="flex-1"
              style="max-height: 10rem"
              [placeholder]="'support.chat.composerPlaceholder' | translate"
              [ngModel]="draft()"
              (ngModelChange)="onDraftChange($event)"
              (keydown)="onComposerKeydown($event)"
            ></textarea>
            <p-button
              icon="fa-solid fa-paper-plane"
              [loading]="supportChat.sending()"
              [disabled]="!draft().trim() || supportChat.sending()"
              (onClick)="send()"
              [ariaLabel]="'support.chat.sending' | translate"
            />
          </div>
        }
      </div>
    </div>
  }
</div>
